/**
 * @fileoverview A single tag row in the tags table, with expand/edit/delete actions.
 * @module features/tags/components/TagRow
 */
import { Fragment, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import { TagChip } from './TagChip'
import { TagProductsPanel, AddTagProductsControl } from './TagProductsPanel'
import type { Tag } from '../schemas/tagSchema'

interface TagRowProps {
  tag: Tag
  expanded: boolean
  onToggle: () => void
  onEdit: () => void
  onDelete: () => void
}

export function TagRow({ tag, expanded, onToggle, onEdit, onDelete }: TagRowProps): ReactElement {
  const { t } = useTranslation('features-tags')

  return (
    <Fragment>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="size-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-4 text-muted-foreground" />
            )}
            <TagChip tag={tag} />
            <span className="text-xs text-muted-foreground">
              {t('page.productCount', { count: tag.productCount })}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
            <AddTagProductsControl tagUuid={tag.uuid} />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('row.editTag', 'Edit tag')}
              onClick={onEdit}
            >
              <Pencil className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('row.deleteTag', 'Delete tag')}
              onClick={onDelete}
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className={cn('hover:bg-transparent')}>
          <TableCell colSpan={2} className="bg-muted/30">
            <TagProductsPanel tagUuid={tag.uuid} />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}
